import WRAP from 'WRAP-JS';
import { parseUTCDate, formatUTCDate } from '../../../utils/FPEUtils';

class ContentsBase {

  constructor(contentsName, dataName, dataConfName) {
    /**
     * コンテンツ名
     * @type {string}
     */
    this.contentsName = contentsName;

    /**
     * 断面図データオブジェクトのキー名
     * @type {string}
     */
    this.dataName = dataName || contentsName;

    /**
     * WRAP.DHにaddObjectする際に指定するdataconfigのファイル名
     */
    this.dataConfName = dataConfName;

    /**
     * データハンドラのインスタンス
     * @type {WRAP.DH.Reference}
     */
    this.dataHandler = undefined;

    /**
     * データハンドラがinspect(データロード時)に返却するオブジェクト
     * @type {*}
     */
    this.dataReference = undefined;

    /**
     * 表示フラグ
     * @type {boolean}
     */
    this.visible = false;

    this.ready = false;

    this.updated = false;
  }

  /**
   * コンテンツ名を取得する.
   * @returns {string}
   */
  getContentsName() {
    return this.contentsName;
  }

  /**
   * 断面図データオブジェクトのキー名を取得する.
   * @returns {string}
   */
  getDataName() {
    return this.dataName;
  }

  /**
   * データハンドラのインスタンスを取得する.
   * @returns {WRAP.DH.Reference}
   */
  getDataHandler() {
    return this.dataHandler;
  }
 
  /**
   * ロードしたデータを取得する.
   * @type {*}
   */
  getDataReference() {
    return this.dataReference;
  }

  /**
   * 表示フラグを取得する.
   * @returns {boolean}
   */
  isVisible() {
    return this.visible;
  }

  /**
   * 表示フラグを設定する.
   * @param {boolean} visible 表示フラグ
   */
  setVisible(visible) {
    this.visible = visible;
    if(this.dataHandler?._data?.conf) {
      if (this.visible) {
        this.updated = false
      };
      this.dataHandler.setUpdateInterval(this.visible?this.dataHandler._data.conf.Attributes.UpdateInterval:0);
    }
  }

  /**
   * 描画の前処理が必要なコンテンツは継承先のクラスで実装する
   */
  beforeDraw() {
  }

  /**
   * 描画の後処理が必要なコンテンツは継承先のクラスで実装する
   */
  afterDraw() {
  }

  /**
   * 画角変更時の処理
   */
  updateBounds() {}

  /**
   * 
   */
  addObject() {
    return new Promise((resolve, reject) => {
      this.dataHandler = WRAP.DH.addObject(this.dataConfName);
      this.dataHandler.inspect(function (ref) {
        try {
          if (this._validate(ref)) {
            this.dataReference = ref;
            this.parseTimelist();
            this.ready = true;
            this.updated = true;
            resolve();
          } else {
            this.addErrorMessage('Configuration validate error.');
            reject();
          }
        } catch (e) {
          this._error(e);
          reject();
        }
      }.bind(this), false)
    });
  }

  /**
   * データのリファレンスが正しく読み込まれたか判定する
   * @param {WRAP.DH.Reference} ref データのリファレンス
   * @returns {boolean}
   * @private
   */
  _validate(ref) {
    return ref.available();
  }

  /**
   * タイムリストをパースする.
   * @param tl タイムリスト
   * @param {number} [basetimeRange] 取得するbasetimeの範囲(時間) 指定しない場合は全取得
   * @param {number} [validtimeRange] 取得するvalidtimeの範囲(時間) 指定しない場合は全取得
   * @returns {Array} タイムリストをパースした配列
   * @private
   */
  _parseTimelist(tl, basetimeRange = 0, validtimeRange) {
    // basetimeで降順ソート
    const sortedTimelist = tl.concat().sort((a, b) => {
      if (a.basetime > b.basetime) {
        return -1
      }else if (a.basetime < b.basetime) {
        return 1
      }
      return 0;
    });

    const list = [];

    let minBasetimeDate;
    if(basetimeRange === 0) {
      minBasetimeDate = parseUTCDate(sortedTimelist[sortedTimelist.length - 1].basetime);
    } else {
      minBasetimeDate = parseUTCDate(sortedTimelist[0].basetime);
      minBasetimeDate.setTime(minBasetimeDate.getTime() - basetimeRange);
    }
    const minBasetimeSec = minBasetimeDate.getTime();

    for (const timelist of sortedTimelist) {
      const baseTimeDate = parseUTCDate(timelist.basetime);
      if(baseTimeDate.getTime() < minBasetimeSec) {
        continue;
      }
      const data = {basetime:formatUTCDate(baseTimeDate), validtime: this._parseValid(timelist.validtime, timelist.basetime, validtimeRange)};
      if(timelist.issuetime) {
        data.issuetime = formatUTCDate(parseUTCDate(timelist.issuetime));
      }
      list.push(data);
    }

    return list;
  }

  _parseValid(validtimeList, basetime, validtimeRange = 0) {
    const sortedTimeList = validtimeList.concat().sort((a, b) => {
      if (a > b) {
        return -1
      }else if (a < b) {
        return 1
      }
      return 0;
    });
   
    const list = [];

    let minDate;
    if(validtimeRange === 0) {
      minDate = parseUTCDate(sortedTimeList[sortedTimeList.length - 1]);
    } else {
      minDate = parseUTCDate(sortedTimeList[0]);
      minDate.setTime(minDate.getTime() - validtimeRange);
    }
    const minMSec = minDate.getTime();

    const basetimeDate = new WRAP.Core.DateTime(basetime);
    for (const validtime of validtimeList) {
      const validtimeDate = parseUTCDate(validtime);
      if(validtimeDate.getTime() < minMSec) {
        continue;
      }

      const validtimeWrapDate = new WRAP.Core.DateTime(validtime);
      const ft = Math.round(basetimeDate.diff(validtimeWrapDate) / 3600);
      list.push([formatUTCDate(validtimeDate), ft]);
    }

    return list;
  }

  /**
   * 予期せぬエラーが発生した場合の処理.
   * @param {Error} e エラーオブジェクト
   * @private
   */
  _error(e) {
    this.setVisible(false);
    console.error(e);
  }

  /**
   * timelistを指定の時刻までに絞る
   * @param {string[]} timelists validtimeのリスト
   * @param {number} [validtimeRange] 取得するvalidtimeの範囲（時間）指定しない場合は全取得
   */
  filterValid(timelists, validtimeRange = 0) {
    // validtimeで降順ソート
    const sortedTimeList = timelists.concat().sort((a, b) => {
      if (a > b) {
        return -1
      }else if (a < b) {
        return 1
      }
      return 0;
    });

    const retValidtimelist = [];

    const minDate = parseUTCDate(sortedTimeList[0]);
    minDate.setTime(minDate.getTime() - validtimeRange);
    const minMSec = minDate.getTime();

    for(const time of sortedTimeList) {
      const dateTime = parseUTCDate(time);
      if(dateTime.getTime() < minMSec) {
        continue;
      }
      retValidtimelist.push(formatUTCDate(dateTime));
    };
    return retValidtimelist;
  }
 
  /**
   * validtimeのリストからdateに最も近いvalidtimeを返す
   * @param {String} validTime
   * @return {Array}
   */
  getValidtimeClosestToTheDateForValid(validTime) {
    let minDiff;
    let index;
    const dateTime = parseUTCDate(validTime).getTime();
    this.validtimeList.forEach((vt, idx) => {
      const validDate = parseUTCDate(vt);
      const temp = Math.abs(dateTime - validDate.getTime());
      if(minDiff === undefined || temp < minDiff) {
        minDiff = temp;
        index = idx;
      }
    });
    return this.validtimeList[index];
  }

    /**
   * validtimeのリストからdateに最も近いvalidtimeを返す
   * @param {Date} date
   * @return {Array}
   */
  getValidtimeClosestToTheDateForBaseValid(validTime, fpMode) {
    let minDiff;
    let index;
    const dateTime = parseUTCDate(validTime).getTime();
    const latestData = this.validtimeList[0];
    if(fpMode) {
      const ret = {basetime: latestData.basetime, validtime: latestData.validtime};
      if(latestData.issuetime) {
        ret.issuetime = latestData.issuetime;
      }
      return ret;
    }
    latestData.validtime.forEach((vt, idx) => {
      const validDate = parseUTCDate(vt[0]);
      const temp = Math.abs(dateTime - validDate.getTime());
      if(minDiff === undefined || temp < minDiff) {
        minDiff = temp;
        index = idx;
      }
    });
    const ret = {basetime: latestData.basetime, validtime: [latestData.validtime[index]]};
    if(latestData.issuetime) {
      ret.issuetime = latestData.issuetime;
    }
    return ret;
  }
  
  isDiffPrevDataValid() {
    if(this.prevValidtime.length !== this.validtimeList.length) {
      return true;
    }
    for(let i = 0; i < this.prevValidtime.length; i++) {
      if(this.prevValidtime[i] !== this.validtimeList[i]) {
        return true;
      }
    }
    return false;
  }
  
  isDiffPrevDataBaseValid() {
    if(this.prevValidtime.length !== this.validtimeList.length) {
      return true;
    }
    for(let i = 0; i < this.prevValidtime.length; i++) {
      if(this.prevValidtime[i].basetime !== this.validtimeList[i].basetime ||
        this.prevValidtime[i].issuetime !== this.validtimeList[i].issuetime ||
        this.prevValidtime[i].validtime.length !== this.validtimeList[i].validtime.length) {
        return true;
      }
      for(let j = 0; j < this.prevValidtime[i].validtime.length; j++) {
        if(this.prevValidtime[i].validtime[j][0] !== this.validtimeList[i].validtime[j][0]) {
          return true;
        }
      }
    }
    return false;
  }

  isReady() {
    return this.ready;
  }
}

export default ContentsBase;